import {Trans} from '@lingui/macro';
import _, {differenceBy} from 'lodash';
import memoize from 'lru-memoize';
import memoizeOne from 'memoize-one';
import React, {Component} from 'react';

import {FrontEvent, Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {AsyncBuffer, AsyncBufferWaiter} from '../../../../helpers/asyncHelpers';
import {buildTeammateDisplayName, doesTeammateMatchQuery} from '../../../../helpers/modelHelpers';
import {CompanyModel} from '../../../../models/api/companyModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {DropdownCategory} from '../../../common/dropdowns/dropdownCategory';
import {DropdownItemAccentIcon} from '../../../common/dropdowns/dropdownItemAccentIcon';
import {DropdownItemTeammateAvatar} from '../../../common/dropdowns/dropdownItemTeammateAvatar';
import {DeprecatedSearchDropdown} from '../../../common/dropdowns/search/deprecatedSearchDropdown';
import {AsyncDropdownSource, DropdownFetcher} from '../../../common/dropdowns/source/asyncDropdownSource';
import {DropdownSource, RowRendererOf} from '../../../common/dropdowns/source/dropdownSource';
import {SyncDropdownSource} from '../../../common/dropdowns/source/syncDropdownSource';
import {workspacesEditorDropdownWidth} from '../editor/context/types';

/*
 * Props.
 */

type WorkspaceTeammatesDropdownSourceBuilder = (
  isUserGlobalAdmin: boolean,
  fetchTeammatesAsync: DropdownFetcher<TeammateModel | null>,
) => DropdownSource;

type ToggleTeammateHandler = (teammate: TeammateModel, saveGate: AsyncBufferWaiter) => void;

interface WorkspaceTeammatesDropdownProps extends DropdownProps {
  teammates: ReadonlyArray<TeammateModel>;
  displayedTeammateIds: ReadonlyArray<number>;
  navigateToAddTeammate: Handler;
  navigateToTeammate: HandlerOf<number>;
  toggleTeammate: ToggleTeammateHandler;
  isUserGlobalAdmin: boolean;
  company: CompanyModel;
  hasLazyTeammatesDropdowns: boolean;
  fetchTeammatesAsync: DropdownFetcher<TeammateModel | null>;
  isEditingWorkspace?: boolean;
}

/*
 * Component.
 */

export class WorkspaceTeammatesDropdown extends Component<WorkspaceTeammatesDropdownProps> {
  constructor(props: WorkspaceTeammatesDropdownProps) {
    super(props);

    this.saveBuffer = new AsyncBuffer();

    const displayedTeammateIdsSet = new Set(props.displayedTeammateIds);
    this.initiallyCheckedTeammates = props.teammates.filter((teammate) =>
      displayedTeammateIdsSet.has(teammate.id),
    );

    this.buildAsyncDropdownSourceMemoized = memoizeOne(this.buildAsyncDropdownSource);
  }

  private readonly saveBuffer: AsyncBuffer;
  private readonly initiallyCheckedTeammates: ReadonlyArray<TeammateModel>;
  private readonly buildAsyncDropdownSourceMemoized: WorkspaceTeammatesDropdownSourceBuilder;

  /*
   * Lifecycle.
   */

  componentWillUnmount() {
    this.saveBuffer.flush();
  }

  /*
   * Event handlers.
   */

  render() {
    const dropdownSource = this.props.hasLazyTeammatesDropdowns
      ? this.buildAsyncDropdownSourceMemoized(this.props.isUserGlobalAdmin, this.props.fetchTeammatesAsync)
      : this.buildSyncDropdownSource();
    return (
      <DeprecatedSearchDropdown
        {...this.props}
        interactionId="workspace-teammates-dropdown"
        title={<Trans id="workspace-teammates">Teammates</Trans>}
        width={this.props.isEditingWorkspace ? workspacesEditorDropdownWidth : 300}
        source={dropdownSource}
      />
    );
  }

  private buildAsyncDropdownSource(
    isUserGlobalAdmin: boolean,
    fetchTeammatesAsync: DropdownFetcher<TeammateModel | null>,
  ) {
    // Non global-admins should not have the option to add teammates (i.e. no fixed rows)
    const fixedRows = isUserGlobalAdmin ? [null, null, null] : [];

    const initiallyUncheckedTeammates = differenceBy(
      this.props.teammates,
      this.initiallyCheckedTeammates,
      (t) => t.id,
    );

    return new AsyncDropdownSource({
      fetcher: fetchTeammatesAsync,
      renderRow: this.renderRow,
      initialItems: this.props.isEditingWorkspace
        ? [...initiallyUncheckedTeammates]
        : [...fixedRows, ...this.initiallyCheckedTeammates],
    });
  }

  private buildSyncDropdownSource() {
    // Non global-admins should not have the option to add teammates (i.e. no fixed rows)
    const fixedRows = this.props.isUserGlobalAdmin ? [null, null, null] : [];
    const initiallyUncheckedTeammates = differenceBy(
      this.props.teammates,
      this.initiallyCheckedTeammates,
      (t) => t.id,
    );
    return new SyncDropdownSource({
      items: this.props.isEditingWorkspace
        ? [...initiallyUncheckedTeammates]
        : [...fixedRows, ...this.initiallyCheckedTeammates, ...initiallyUncheckedTeammates],
      rowFilter: filterRows,
      renderRow: this.renderRow,
    });
  }

  private readonly renderRow: RowRendererOf<TeammateModel | null> = (teammate, index) => {
    if (!teammate) {
      return this.renderFixedRow(index);
    }

    return this.renderTeammateRow(teammate, index - 2);
  };

  private renderFixedRow(index: number) {
    switch (index) {
      case 0:
        return (
          <DropdownCategory>
            <Trans id="workspace-category-invite">Invite</Trans>
          </DropdownCategory>
        );

      case 1:
        return (
          <DropdownItem
            onSelect={this.props.navigateToAddTeammate}
            index={0}
            interactionId="workspace-teammates-dropdown-new-teammate"
          >
            <DropdownItemAccentIcon label={null} iconName="dropdownNew" />
            <DropdownItemContent isEditingWorkspace={this.props.isEditingWorkspace}>
              <Trans id="workspace-newteammate">New teammate</Trans>
            </DropdownItemContent>
          </DropdownItem>
        );

      case 2:
        return (
          <DropdownCategory>
            <Trans id="workspace-category-teammates">Teammates</Trans>
          </DropdownCategory>
        );

      default:
        throw new Error('This should not happen.');
    }
  }

  private isTeammateChecked(teammate: TeammateModel) {
    return this.props.displayedTeammateIds.includes(teammate.id);
  }

  private renderTeammateRow(teammate: TeammateModel, index: number) {
    const {toggleTeammate} = this.props;
    const isChecked = this.isTeammateChecked(teammate);
    const onTeammateSelect: HandlerOf<FrontEvent> = (e) => {
      e.preventDefault(); // Keep the dropdown open
      toggleTeammate(teammate, this.saveBuffer.buildWaiter());
    };

    return (
      <DropdownItem
        index={index}
        onSelect={onTeammateSelect}
        renderRightContent={() => <DropdownItemCheckbox isChecked={isChecked} />}
      >
        <DropdownItemTeammateAvatar teammate={teammate} />
        <DropdownItemContent isChecked={isChecked}>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
      </DropdownItem>
    );
  }
}

const filterRows = memoize(20)((teammates: ReadonlyArray<TeammateModel | null>, query: string) => {
  // Shorcut for empty query.
  if (!query) {
    return teammates;
  }

  // Otherwise, filter by name.
  return _(teammates)
    .compact()
    .filter((t) => doesTeammateMatchQuery(t, query))
    .value();
});
